import React, { useMemo, useState } from 'react';
import { X, Plus } from '@phosphor-icons/react';
import { useApp } from '../../contexts/AppContext';
import { useVendorQuotes } from '../../hooks/useVendorQuotes';
import { useData } from '../../hooks/useData';
import { TABLES, MODAL_TYPES } from '../../constants';
import { formatCurrency } from '../../utils/formatters';
import { getLineTotal } from '../../utils/boqHelpers';
import QuoteRow from './QuoteRow';
import QuoteDetailView from './QuoteDetailView';
import styles from './TradeQuotesModal.module.css';

/**
 * TradeQuotesModal — Per-trade quote management modal.
 *
 * Opened from NeedsQuotesTab when the user clicks the "הצעות מחיר" button on a trade group.
 * Shows competing quotes that overlap the trade's unquoted project items.
 *
 * Modes:
 * - 'list'  : lists QuoteRow components with row-level actions (select, delete, share)
 * - 'detail': shows QuoteDetailView for a selected quote
 *
 * Handoff: "+ צור הצעה חדשה" closes this modal and opens QuoteFormModal
 *          pre-configured for the trade (vendor type + preselected items).
 *
 * Props (via modalState.data):
 * - projectId
 * - tradeName            : string — display only
 * - tradeVendorTypeId    : string|null — null for __unclassified__
 * - projectItemIds       : string[] — unquoted project_item IDs for this trade
 */
const TradeQuotesModal = ({ projectId, tradeName, tradeVendorTypeId, projectItemIds = [] }) => {
  const { closeModal, openModal, showSuccess, showError } = useApp();
  const {
    quotes,
    quoteItems,
    isLoading,
    selectQuote,
    removeQuote,
    updateQuote,
    generateShareLink,
  } = useVendorQuotes(projectId);

  const [viewMode, setViewMode] = useState('list');
  const [detailQuoteId, setDetailQuoteId] = useState(null);
  const [copiedQuoteId, setCopiedQuoteId] = useState(null);

  // Vendor lookup
  const { data: masterVendors } = useData(TABLES.MASTER_VENDORS, {
    select: 'id, name, contact_person, vendor_type_id',
    enabled: !!projectId,
  });

  const vendorMap = useMemo(
    () => new Map((masterVendors || []).map(v => [v.id, v])),
    [masterVendors],
  );

  const projectItemIdSet = useMemo(() => new Set(projectItemIds), [projectItemIds]);

  // Filter to quotes whose line items overlap this trade's project items
  const tradeQuotes = useMemo(() => {
    if (!quotes || !quoteItems) return [];
    const relevantQuoteIds = new Set();
    quoteItems.forEach(line => {
      if ((line.project_item_ids || []).some(id => projectItemIdSet.has(id))) {
        relevantQuoteIds.add(line.quote_id);
      }
    });
    return quotes
      .filter(q => relevantQuoteIds.has(q.id))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [quotes, quoteItems, projectItemIdSet]);

  // Compute per-quote priced count for the badge in QuoteRow
  const quoteProgressMap = useMemo(() => {
    const map = new Map();
    if (!quoteItems) return map;
    tradeQuotes.forEach(q => {
      const lines = quoteItems.filter(qi => qi.quote_id === q.id);
      const totalItems = lines.length;
      const pricedCount = lines.filter(qi => qi.line_price != null).length;
      map.set(q.id, { pricedCount, totalItems });
    });
    return map;
  }, [tradeQuotes, quoteItems]);

  // Compute total from line items (fallback to quote.total_price handled inside QuoteRow)
  const quoteTotalMap = useMemo(() => {
    const map = new Map();
    if (!quoteItems) return map;
    tradeQuotes.forEach(q => {
      const lines = quoteItems.filter(qi => qi.quote_id === q.id);
      const sum = lines.reduce((acc, qi) => {
        if (qi.line_price == null) return acc;
        const dd = qi.display_data || {};
        return acc + getLineTotal(Number(qi.line_price), dd.quantity || 1, dd.cost_type_name || '');
      }, 0);
      map.set(q.id, sum > 0 ? sum : null);
    });
    return map;
  }, [tradeQuotes, quoteItems]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleCreateNew = () => {
    closeModal();
    openModal(MODAL_TYPES.QUOTE_FORM, {
      projectId,
      preselectedItems: projectItemIds,
      defaultVendorTypeId: tradeVendorTypeId,
      initialMode: 'selected-only',
    });
  };

  const handleStatusChange = async (quote, newStatus) => {
    try {
      if (newStatus === 'selected') {
        await selectQuote(quote.id);
      } else {
        await updateQuote({ id: quote.id, data: { status: newStatus } });
        showSuccess('הסטטוס עודכן');
      }
    } catch (err) {
      showError(err?.message || 'שגיאה בעדכון הסטטוס');
    }
  };

  const handleDelete = async quote => {
    if (!window.confirm('למחוק את הצעת המחיר?')) return;
    try {
      await removeQuote(quote.id);
      showSuccess('ההצעה נמחקה');
    } catch (err) {
      showError(err?.message || 'שגיאה במחיקת ההצעה');
    }
  };

  const handleShare = async quote => {
    try {
      const url = await generateShareLink(quote.id);
      await navigator.clipboard.writeText(url);
      setCopiedQuoteId(quote.id);
      showSuccess('הקישור הועתק ללוח');
      setTimeout(() => setCopiedQuoteId(null), 2000);
    } catch (err) {
      showError(err?.message || 'שגיאה ביצירת קישור');
    }
  };

  const handleOpenDetail = quote => {
    setDetailQuoteId(quote.id);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setDetailQuoteId(null);
    setViewMode('list');
  };

  // ─── Detail view data shaping ────────────────────────────────────────────

  const detailQuote = useMemo(
    () => tradeQuotes.find(q => q.id === detailQuoteId) || null,
    [tradeQuotes, detailQuoteId],
  );

  const detailData = useMemo(() => {
    if (!detailQuote || !quoteItems) return null;

    const lines = quoteItems.filter(qi => qi.quote_id === detailQuote.id);

    // Build items from quote line items using the display_data snapshot
    const items = lines.map((qi, idx) => {
      const dd = qi.display_data || {};
      const qty = dd.quantity || 1;
      const costTypeName = dd.cost_type_name || '';
      const linePrice = qi.line_price != null ? Number(qi.line_price) : null;
      const lineTotal = linePrice != null ? getLineTotal(linePrice, qty, costTypeName) : null;

      return {
        id: qi.id || `line_${idx}`,
        name: dd.item_name || dd.name || 'פריט',
        unitLabel: dd.unit_label || '—',
        quantity: qty,
        linePrice,
        lineTotal,
        lineNotes: qi.line_notes || null,
        isSkipped: qi.line_price == null,
      };
    });

    const sum = items.reduce((acc, it) => acc + (it.lineTotal || 0), 0);
    const pricedCount = items.filter(it => !it.isSkipped).length;
    const skippedCount = items.filter(it => it.isSkipped).length;

    const groups = [
      {
        id: 'trade_items',
        headerLabel: tradeName,
        groupTotal: sum,
        items,
      },
    ];

    const totals = {
      sum,
      pricedCount,
      skippedCount,
      totalItems: items.length,
    };

    return { groups, totals };
  }, [detailQuote, quoteItems, tradeName]);

  const detailVendor = detailQuote ? vendorMap.get(detailQuote.master_vendor_id) : null;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className={styles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>הצעות מחיר — {tradeName}</h2>
          <p className={styles.subtitle}>
            {projectItemIds.length} פריטים • {tradeQuotes.length} הצעות
          </p>
        </div>
        <button className={styles.closeBtn} onClick={closeModal} type="button" aria-label="סגור">
          <X size={20} />
        </button>
      </header>

      <div className={styles.body}>
        {viewMode === 'detail' && detailQuote && detailData ? (
          <QuoteDetailView
            vendorName={detailVendor?.name}
            vendorContact={detailVendor?.contact_person}
            groups={detailData.groups}
            totals={detailData.totals}
            onBack={handleBackToList}
            backLabel="חזרה לרשימת ההצעות"
            showUnitAndQty={true}
          />
        ) : (
          <>
            {isLoading ? (
              <div className={styles.state}>טוען הצעות...</div>
            ) : tradeQuotes.length === 0 ? (
              <div className={styles.state}>
                <p className={styles.emptyTitle}>אין עדיין הצעות מחיר לקטגוריה זו</p>
                <p className={styles.emptyHint}>צור הצעה חדשה כדי להתחיל לאסוף מחירים מספקים.</p>
              </div>
            ) : (
              <div className={styles.quoteList}>
                {tradeQuotes.map(q => {
                  const vendor = vendorMap.get(q.master_vendor_id);
                  return (
                    <QuoteRow
                      key={q.id}
                      quote={q}
                      vendorName={vendor?.name}
                      priced={quoteProgressMap.get(q.id)}
                      totalOverride={quoteTotalMap.get(q.id)}
                      showVATTotal={false}
                      copied={copiedQuoteId === q.id}
                      onClick={() => handleOpenDetail(q)}
                      onShare={() => handleShare(q)}
                      onStatusChange={newStatus => handleStatusChange(q, newStatus)}
                      onDelete={() => handleDelete(q)}
                    />
                  );
                })}
              </div>
            )}

            <button type="button" className={styles.createBtn} onClick={handleCreateNew}>
              <Plus size={18} />
              צור הצעה חדשה
            </button>
            <p className={styles.createHint}>
              * יצירת הצעה חדשה תסגור את החלון הזה ותפתח את אשף יצירת הצעת המחיר.
            </p>
          </>
        )}
      </div>

      {viewMode === 'list' && tradeQuotes.length > 0 && (
        <footer className={styles.footer}>
          <span className={styles.footerSummary}>
            סה״כ {tradeQuotes.length} הצעות •{' '}
            {formatCurrency(
              tradeQuotes.reduce((acc, q) => acc + (quoteTotalMap.get(q.id) || 0), 0),
            )}
          </span>
        </footer>
      )}
    </div>
  );
};

export default TradeQuotesModal;
